import { useState, useEffect } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';

const ROLES = ['admin', 'beta_tester'];

export default function UsersTab() {
  const { authFetch } = useAuthFetch();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState({});

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/admin/users`)
      .then(res => res.json())
      .then(data => { setUsers(data); setLoading(false); })
      .catch(err => { console.error(err); setError('Failed to load users.'); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRole = async (userId, role, hasRole) => {
    const key = `${userId}-${role}`;
    setToggling(prev => ({ ...prev, [key]: true }));
    try {
      const method = hasRole ? 'DELETE' : 'POST';
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/roles/${role}`,
        { method }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setUsers(prev => prev.map(u => {
        if (u.id !== userId) return u;
        const roles = hasRole ? u.roles.filter(r => r !== role) : [...u.roles, role];
        return { ...u, roles };
      }));
    } catch (err) {
      console.error('Failed to toggle role:', err);
    } finally {
      setToggling(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) return <p className="costs-loading">Loading users…</p>;
  if (error) return <p className="costs-error">{error}</p>;

  return (
    <div>
      <div className="costs-section">
        <p className="costs-section-title">Users ({users.length})</p>
        <div className="costs-table-wrapper">
          <table className="costs-table">
            <thead>
              <tr>
                <th>Name / ID</th>
                <th>Member Since</th>
                <th>Roles</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>
                    <span style={{ fontWeight: 500 }}>
                      {user.display_name || `User #${user.id}`}
                    </span>
                    <br />
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-subtle)', fontFamily: 'monospace' }}>
                      {user.azure_oid.slice(0, 8)}…
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {ROLES.map(role => {
                        const hasRole = user.roles.includes(role);
                        const key = `${user.id}-${role}`;
                        return (
                          <button
                            key={role}
                            onClick={() => toggleRole(user.id, role, hasRole)}
                            disabled={toggling[key]}
                            style={{
                              padding: '2px 10px',
                              borderRadius: '999px',
                              fontSize: '0.78rem',
                              fontWeight: 500,
                              border: '1px solid',
                              cursor: toggling[key] ? 'wait' : 'pointer',
                              transition: 'background 0.15s, color 0.15s',
                              background: hasRole ? 'var(--color-accent-bg-2)' : 'var(--color-surface)',
                              color: hasRole ? 'var(--color-text)' : 'var(--color-text-subtle)',
                              borderColor: hasRole ? 'rgba(249,115,22,0.55)' : 'var(--color-border-strong)',
                            }}
                          >
                            {role.replace('_', ' ')}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
